import type { FeatureCollection } from 'geojson'
import { ColumnDefinition } from 'src/types/tableDataSet'
import type { ValueOverride } from 'src/types/dashboard'

export interface CategoriesSeriesResult {
  categories: string[]
  series: { name: string; data: number[]; color?: string }[]
}

export interface PieSliceResult {
  name: string
  value: number
  itemStyle?: { color?: string }
}

export interface MapSliceResult {
  name: string
  value: number | string
}

export interface MapTransformerResult {
  slices: MapSliceResult[]
  geoJsonData: FeatureCollection | null
  customColorMap: Map<string, string>
}

export const isGeoJsonColumn = (col?: ColumnDefinition | null): boolean => {
  if (!col) return false
  return (
    col.uiType === 'geojson' ||
    (col.uiType === 'json' && col.name.startsWith('geometry_'))
  )
}

export const isNumericValue = (val: unknown): boolean => {
  if (val === null || val === undefined) return false
  if (typeof val === 'number') return !Number.isNaN(val)
  const str = String(val).trim()
  if (str === '') return false
  return !Number.isNaN(Number(str))
}

export const isNumericDataset = (values: unknown[]): boolean => {
  const validValues = values.filter(
    (v) => v !== null && v !== undefined && String(v).trim() !== ''
  )
  return validValues.length > 0 && validValues.every(isNumericValue)
}

const toNumber = (value: unknown): number => {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : 0
}

const parseValue = (value: unknown): number | string => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0

  const str = String(value).trim()
  if (str === '') return ''

  const num = Number(str)
  return Number.isNaN(num) ? str : num
}

const isValidHex = (color?: string): boolean => {
  if (!color) return false
  const str = color.trim()
  return /^#([0-9A-F]{3,4}|[0-9A-F]{6}|[0-9A-F]{8})$/i.test(str)
}

export const toCategoriesSeries = (
  data: Record<string, unknown>[],
  xAxis: string[],
  yAxis: string[],
  overrides?: Record<string, ValueOverride>
): CategoriesSeriesResult => {
  const xColumn = xAxis[0]
  if (!xColumn) return { categories: [], series: [] }

  const categories = data.map((row) => String(row[xColumn] ?? ''))

  const series = yAxis.map((yColumn) => {
    const override = overrides?.[yColumn]
    const color = isValidHex(override?.color)
      ? override?.color?.trim()
      : undefined

    return {
      name: override?.label || yColumn,
      color,
      data: data.map((row) => toNumber(row[yColumn]))
    }
  })

  return { categories, series }
}

export const toPieSlices = (
  data: Record<string, unknown>[],
  xAxis: string[],
  yAxis: string[],
  overrides?: Record<string, ValueOverride>
): PieSliceResult[] => {
  const xColumn = xAxis[0]
  const yColumn = yAxis[0]
  if (!xColumn || !yColumn) return []

  return data.map((row) => {
    const rawName = String(row[xColumn] ?? '')
    const override = overrides?.[rawName]
    const color = isValidHex(override?.color)
      ? override?.color?.trim()
      : undefined

    return {
      name: override?.label || rawName,
      value: toNumber(row[yColumn]),
      ...(color ? { itemStyle: { color } } : {})
    }
  })
}

export const toMapSlices = (
  data: Record<string, unknown>[],
  xAxis: string[],
  yAxis: string[],
  colSpec?: ColumnDefinition[] | null,
  geoColumn?: string,
  overrides?: Record<string, ValueOverride>
): MapTransformerResult => {
  const xColumn = xAxis[0]
  const yColumn = yAxis[0]

  if (!xColumn || !yColumn || !data.length) {
    return { slices: [], geoJsonData: null, customColorMap: new Map() }
  }

  const targetGeoCol = geoColumn || colSpec?.find(isGeoJsonColumn)?.name

  if (!targetGeoCol) {
    return { slices: [], geoJsonData: null, customColorMap: new Map() }
  }

  const featuresMap = new Map<string, FeatureCollection['features'][0]>()
  const slicesMap = new Map<string, MapSliceResult>()
  const customColorMap = new Map<string, string>()

  for (const row of data) {
    const regionName = String(row[xColumn] ?? '').trim()
    if (!regionName) continue

    const metricValue = parseValue(row[yColumn])
    const rawValStr = String(metricValue).trim()
    const override = overrides?.[rawValStr]

    const displayValue =
      typeof metricValue === 'string' && override?.label
        ? override.label
        : metricValue

    if (
      !slicesMap.has(regionName) ||
      (slicesMap.get(regionName)?.value === '' && displayValue !== '')
    ) {
      slicesMap.set(regionName, { name: regionName, value: displayValue })
    }

    const catKey = String(displayValue).trim()
    if (isValidHex(override?.color) && !customColorMap.has(catKey)) {
      customColorMap.set(catKey, override!.color!.trim())
    }

    const rawGeo = row[targetGeoCol]
    if (rawGeo && !featuresMap.has(regionName)) {
      try {
        const geometry =
          typeof rawGeo === 'string' ? JSON.parse(rawGeo) : rawGeo
        featuresMap.set(regionName, {
          type: 'Feature',
          geometry,
          properties: { name: regionName }
        })
      } catch {
        // Ignore invalid JSON
      }
    }
  }

  if (isValidHex(overrides?.['__map_min__']?.color)) {
    customColorMap.set('__map_min__', overrides!['__map_min__']!.color!.trim())
  }
  if (isValidHex(overrides?.['__map_max__']?.color)) {
    customColorMap.set('__map_max__', overrides!['__map_max__']!.color!.trim())
  }

  return {
    slices: Array.from(slicesMap.values()),
    geoJsonData: {
      type: 'FeatureCollection',
      features: Array.from(featuresMap.values())
    },
    customColorMap
  }
}

const toCategoryString = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

export function buildCategoryColorMap(
  categories: string[],
  colors: string[],
  customColorMap?: Map<string, string>
): Map<string, string> {
  const colorMap = new Map<string, string>()

  categories.forEach((cat, index) => {
    const customColor = customColorMap?.get(cat)

    if (isValidHex(customColor)) {
      colorMap.set(cat, customColor!.trim())
    } else {
      colorMap.set(cat, colors[index % colors.length])
    }
  })

  return colorMap
}

export function getUniqueCategories(data: MapSliceResult[]): string[] {
  return Array.from(
    new Set(data.map((d) => toCategoryString(d.value)).filter((v) => v !== ''))
  )
}

export function formatMapSeriesData(
  data: MapSliceResult[],
  categoryColorMap: Map<string, string>,
  categoryIndexMap?: Map<string, number>
) {
  return data.map((d) => {
    const cat = toCategoryString(d.value)
    const color = categoryColorMap.get(cat) || '#2b2d38'
    const val = categoryIndexMap ? (categoryIndexMap.get(cat) ?? -1) : cat

    return {
      name: d.name,
      value: val,
      itemStyle: { areaColor: color },
      emphasis: { itemStyle: { areaColor: color } },
      select: { itemStyle: { areaColor: color } }
    }
  })
}

function hexToRgb(hex: string): [number, number, number] {
  let c = hex.replace('#', '').trim()
  if (c.length === 3)
    c = c
      .split('')
      .map((x) => x + x)
      .join('')
  const num = parseInt(c, 16)
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255]
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map((x) => Math.round(x).toString(16).padStart(2, '0'))
    .join('')}`
}

function blendRgb(
  rgb1: [number, number, number],
  rgb2: [number, number, number],
  ratio: number
): string {
  const r = rgb1[0] + (rgb2[0] - rgb1[0]) * ratio
  const g = rgb1[1] + (rgb2[1] - rgb1[1]) * ratio
  const b = rgb1[2] + (rgb2[2] - rgb1[2]) * ratio
  return rgbToHex(r, g, b)
}

function interpolateColor(
  value: number,
  min: number,
  max: number,
  rgbColors: [number, number, number][]
): string {
  if (!rgbColors.length) return '#2b2d38'
  if (rgbColors.length === 1 || min === max) return rgbToHex(...rgbColors[0])
  const normalized = Math.max(0, Math.min(1, (value - min) / (max - min)))
  const count = rgbColors.length - 1
  const scaled = normalized * count
  const idx = Math.floor(scaled)
  const t = scaled - idx
  const c1 = rgbColors[Math.min(idx, count)]
  const c2 = rgbColors[Math.min(idx + 1, count)]
  return blendRgb(c1, c2, t)
}

export function formatNumericMapSeriesData(
  data: MapSliceResult[],
  minVal: number,
  maxVal: number,
  colors: string[]
) {
  const rgbColors = colors.map(hexToRgb)

  return data.map((d) => {
    const num = Number(d.value)
    const color = Number.isNaN(num)
      ? '#2b2d38'
      : interpolateColor(num, minVal, maxVal, rgbColors)

    return {
      name: d.name,
      value: d.value,
      itemStyle: { areaColor: color },
      emphasis: { itemStyle: { areaColor: color } },
      select: { itemStyle: { areaColor: color } }
    }
  })
}

export const getUniqueColumnValues = (
  data: Record<string, unknown>[] | undefined,
  columnName: string
): string[] => {
  if (!data?.length || !columnName) return []
  const uniqueValues = new Set<string>()
  for (const row of data) {
    const val = String(row[columnName] ?? '').trim()
    if (val) uniqueValues.add(val)
  }
  return Array.from(uniqueValues)
}
